// Columna central del explorer: barra de buckets, contador del resultado y la tabla de 9 columnas
// del mock, pintada con el `DataTable` compartido, más los controles de página y el «Cargar más».
//
// Todo lo que se cuenta y se filtra es sobre lo YA CARGADO. Mientras `hasNextPage` siga en `true`
// los rótulos lo declaran (ESTANDAR-TABLAS §5.4); cuando ya no quedan páginas se caen solos.
//
// El rótulo de a quién no lista el padrón no se cae nunca: no depende de cuánto se haya cargado.
import type { ReactNode } from 'react';
import { AlertTriangle, CircleAlert } from 'lucide-react';
import type { Table } from '@tanstack/react-table';
import { ErrorState } from '@/kit';
import { cn } from '@/lib/cn';
import { CellText, DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    CUSTOMERS_NOUN,
    LoadedScopeFooter,
    loadedCountLabel,
    loadedCountTitle,
    loadedCountsNote,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import { SegmentPill, TierPill } from '@/shared/patterns/Pills';
import { FALLBACK_COLOR_SLOT } from '@/shared/patterns/Pills/segmentColors';
import type { CreditCustomerCredit } from '@/api/services/loan-credit';
import {
    CANCELLED_ROW_NOTE,
    CREDIT_ROW_GAP_LABEL,
    CREDIT_ROW_GAP_NOTE,
    creditRowCounts,
    creditRowFactsOf,
    creditRowStatusOf,
    hasActiveCredit,
    type CreditRowGap,
    type CreditRowStatus,
} from '../lib/creditCustomerCredit';
import {
    nextSort,
    type CreditBucket,
    type CreditCustomerRow,
    type SortKey,
    type SortState,
} from '../lib/creditCustomersFilters';
import { ROSTER_SCOPE_NOTE } from '../lib/creditCustomersLoaded';
import {
    ACTIVE_CREDITS_COLUMN_NOTE,
    activeCreditsAnomalyNote,
    BALANCE_COLUMN_NOTE,
    CUSTOMER_STATUS_CHIP_CLASS,
    customerStatusLabel,
    customerStatusNote,
    EMPTY_CELL,
    formatMoneyShortCents,
    LAST_PAYMENT_COLUMN_NOTE,
    LAST_PAYMENT_EMPTY_NOTE,
    lastPaymentLabelOf,
    LEVEL_COLUMN_NOTE,
    levelMissingNote,
    NO_SEGMENTS_NOTE,
    SCI_COLUMN_NOTE,
    sciMissingNote,
    sciSourceNote,
    SEGMENT_COLUMN_NOTE,
    SEGMENTS_UNREAD_LABEL,
    SEGMENTS_UNREAD_NOTE,
    shortCustomerId,
    TOTAL_DEBT_COLUMN_NOTE,
    TOTAL_DEBT_MISSING_NOTE,
    UNNAMED_CUSTOMER,
    UNNAMED_CUSTOMER_NOTE,
} from '../lib/creditCustomersFormat';
import { emptyListNote, listErrorCopy } from '../lib/creditCustomersErrors';

/** Los tres buckets del mock. Cuentan sobre lo CARGADO, y el rótulo lo dice mientras falten páginas. */
const BUCKETS: {
    value: CreditBucket;
    label: string;
    title: string;
    countKey: 'active' | 'arrears' | null;
    /** Color del mock. */
    countClass?: string;
}[] = [
    { value: 'all', label: 'Todos', title: 'Todos los clientes del padrón', countKey: null },
    {
        value: 'active',
        label: 'Con activos',
        title: 'Clientes con al menos un crédito vigente',
        countKey: 'active',
        countClass: 'text-success',
    },
    {
        value: 'arrears',
        label: 'En mora',
        title: 'Clientes con al menos una cuota vencida sin pagar',
        countKey: 'arrears',
        countClass: 'text-destructive',
    },
];

/**
 * El reparto de las 9 columnas del mock. En porcentaje y no en píxeles porque la banda de la lista
 * se angosta con la ventana, y unos anchos fijos escondían Saldo y Estado detrás de un scroll.
 */
const COLUMN_WIDTH = {
    id: '9.5%',
    name: '15%',
    level: '9%',
    segment: '11.5%',
    sci: '8%',
    credits: '7%',
    balance: '11%',
    status: '12%',
    lastPayment: '17%',
} as const;

// Aire justo: con un chip más ancho que su columna, el corte de la celda le pegaba unos
// puntos suspensivos al lado.
const CHIP =
    'inline-flex max-w-full items-center whitespace-nowrap rounded-full border px-1 py-0.5 text-[9.5px] font-semibold';

export function CreditCustomersTable({
    table,
    pageRows,
    allRows,
    filteredCount,
    sort,
    onSortChange,
    bucket,
    onBucketChange,
    selectedId,
    onSelect,
    isLoading,
    hasFilters,
    error,
    onRetry,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    nextPageError,
}: {
    table: Table<CreditCustomerRow>;
    /** Las filas de la página que se está viendo, ya filtradas y ordenadas. */
    pageRows: CreditCustomerRow[];
    /** TODO lo cargado, sin filtrar: sobre eso cuentan las pastillas. */
    allRows: CreditCustomerRow[];
    /** Cuántas filas pasan los filtros sobre lo cargado. */
    filteredCount: number;
    sort: SortState;
    onSortChange: (next: SortState) => void;
    bucket: CreditBucket;
    onBucketChange: (next: CreditBucket) => void;
    selectedId: string | null;
    onSelect: (retailCustomerId: string) => void;
    isLoading: boolean;
    /** Hay filtros puestos: distingue «no hay clientes» de «ninguno coincide». */
    hasFilters: boolean;
    error: Error | null;
    onRetry: () => void;
    /** Quedan páginas en el servidor: nada de lo que se cuenta acá es un total. */
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    /** Por qué falló la ÚLTIMA página pedida. `null` = no falló; lo cargado sigue en la tabla. */
    nextPageError: string | null;
}) {
    const copy = error ? listErrorCopy(error) : null;
    // Sin lista leída no hay universo que contar: la barra pintaría ceros que se leen como
    // «ningún cliente tiene crédito», encima del error.
    if (copy) {
        return (
            <div className="min-h-0 flex-1 overflow-auto">
                <ErrorState
                    className="m-3"
                    title={copy.title}
                    description={copy.description}
                    onRetry={copy.canRetry ? onRetry : undefined}
                />
            </div>
        );
    }

    const scope: LoadedScope = { loaded: allRows.length, hasMore: hasNextPage };
    const counts = creditRowCounts(allRows);
    const countsNote = loadedCountsNote(scope, CUSTOMERS_NOUN);

    return (
        <>
            {/* .stats-bar — los buckets son conteo y filtro a la vez */}
            <div className="flex min-h-10 flex-none items-center gap-1 border-b border-border px-2 py-1.5">
                {BUCKETS.map((item) => (
                    <BucketPill
                        key={item.value}
                        label={item.label}
                        title={loadedCountTitle(item.title, scope, CUSTOMERS_NOUN)}
                        on={bucket === item.value}
                        valueClassName={item.countClass}
                        value={
                            isLoading
                                ? '…'
                                : loadedCountLabel(
                                      item.countKey === null
                                          ? allRows.length
                                          : counts[item.countKey],
                                      scope,
                                  )
                        }
                        onClick={() => {
                            onBucketChange(item.value);
                        }}
                    />
                ))}
            </div>

            {/* El rótulo del padrón se queda siempre; el de lo cargado solo mientras falten páginas. */}
            {isLoading ? null : (
                <div
                    role="status"
                    className="flex-none space-y-0.5 border-b border-border bg-muted/40 px-3 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    <p>{ROSTER_SCOPE_NOTE}</p>
                    {countsNote === null ? null : <p>{countsNote}</p>}
                </div>
            )}

            <DataTable
                columns={customerColumns(hasTotalDebt(allRows))}
                rows={pageRows}
                rowKey={(row) => row.retailCustomerId}
                sort={sort}
                onSortToggle={(key) => {
                    onSortChange(nextSort(sort, key));
                }}
                onRowClick={(row) => {
                    onSelect(row.retailCustomerId);
                }}
                isRowSelected={(row) => row.retailCustomerId === selectedId}
                isLoading={isLoading}
                empty={emptyListNote({ hasFilters })}
            />

            <LoadedScopeFooter
                noun={CUSTOMERS_NOUN}
                scope={isLoading ? null : scope}
                shown={isLoading ? null : filteredCount}
                table={table}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={onLoadMore}
                nextPageError={nextPageError}
            />
        </>
    );
}

/** Con alguna fila que traiga la deuda total, la columna «Saldo» pasa a ser «Deuda total». */
export function hasTotalDebt(rows: readonly CreditCustomerRow[]): boolean {
    return rows.some((row) => row.credit.totalDebtCents !== undefined);
}

/** Las 9 columnas del mock, en su orden. */
function customerColumns(withTotalDebt: boolean): DataTableColumn<CreditCustomerRow, SortKey>[] {
    return [
        {
            key: 'id',
            label: 'ID',
            width: COLUMN_WIDTH.id,
            sortKey: 'id',
            cell: (row) => shortCustomerId(row.retailCustomerId),
            // Cortado en la columna, entero en el `title`: el id es la clave con la que el
            // analista busca al cliente, y unos puntos suspensivos lo dejarían inservible.
            cellTitle: (row) => row.retailCustomerId,
            cellClassName: () => 'cursor-help font-mono text-[9.5px] text-muted-foreground',
        },
        {
            key: 'name',
            label: 'Nombre',
            width: COLUMN_WIDTH.name,
            sortKey: 'name',
            cellTitle: (row) => row.fullName ?? undefined,
            cell: (row) =>
                row.fullName ? (
                    <span className="font-semibold">{row.fullName}</span>
                ) : (
                    /* El nombre IDENTIFICA la fila: un guion la vuelve inidentificable de un
                       vistazo. Las columnas de dato sí van a guion. */
                    <span
                        title={UNNAMED_CUSTOMER_NOTE}
                        className="cursor-help italic text-muted-foreground"
                    >
                        {UNNAMED_CUSTOMER}
                    </span>
                ),
        },
        creditColumn(
            {
                key: 'level',
                label: 'Nivel',
                width: COLUMN_WIDTH.level,
                sortKey: 'level',
                note: LEVEL_COLUMN_NOTE,
            },
            (credit) => (
                <TierPill
                    level={credit.clientLevel}
                    title={
                        credit.clientLevel === null
                            ? levelMissingNote(credit.creditsCount)
                            : undefined
                    }
                    className={credit.clientLevel === null ? 'cursor-help' : undefined}
                />
            ),
        ),
        {
            key: 'segment',
            label: 'Segmento',
            width: COLUMN_WIDTH.segment,
            note: SEGMENT_COLUMN_NOTE,
            cell: (row) => <SegmentCell row={row} />,
            // La marca de un hueco es un icono: sin este `title` el motivo no llega a nadie.
            cellTitle: (row) => segmentGapNoteOf(row),
            cellClassName: (row) =>
                segmentGapNoteOf(row) === undefined ? undefined : 'cursor-help',
        },
        creditColumn(
            {
                key: 'sci',
                label: 'SCI',
                width: COLUMN_WIDTH.sci,
                numeric: true,
                sortKey: 'sci',
                note: SCI_COLUMN_NOTE,
            },
            (credit) =>
                credit.sciScore === null ? (
                    <EmptyValue note={sciMissingNote(credit.creditsCount)} />
                ) : (
                    <span
                        title={sciSourceNote(
                            credit.sciSource === 'BORROWER' ? 'BORROWER' : 'CREDIT_ORIGINATION',
                        )}
                        className="cursor-help font-semibold"
                    >
                        {credit.sciScore}
                    </span>
                ),
        ),
        creditColumn(
            {
                key: 'credits',
                label: 'Créd.',
                width: COLUMN_WIDTH.credits,
                numeric: true,
                sortKey: 'activeCredits',
                note: ACTIVE_CREDITS_COLUMN_NOTE,
            },
            (credit) =>
                credit.activeCreditsCount > 1 ? (
                    <span
                        title={activeCreditsAnomalyNote(credit.activeCreditsCount)}
                        className="inline-flex cursor-help items-center gap-0.5 font-semibold text-warning"
                    >
                        <AlertTriangle className="size-2.5" aria-hidden />
                        {credit.activeCreditsCount}
                    </span>
                ) : (
                    credit.activeCreditsCount
                ),
        ),
        withTotalDebt
            ? creditColumn(
                  {
                      key: 'balance',
                      label: 'Deuda total',
                      width: COLUMN_WIDTH.balance,
                      numeric: true,
                      sortKey: 'balance',
                      note: TOTAL_DEBT_COLUMN_NOTE,
                  },
                  (credit) => <TotalDebtValue credit={credit} />,
              )
            : creditColumn(
                  {
                      key: 'balance',
                      label: 'Saldo',
                      width: COLUMN_WIDTH.balance,
                      numeric: true,
                      sortKey: 'balance',
                      note: BALANCE_COLUMN_NOTE,
                  },
                  (credit) =>
                      hasActiveCredit(credit) && credit.outstandingBalanceCents !== null ? (
                          formatMoneyShortCents(credit.outstandingBalanceCents)
                      ) : (
                          <EmptyValue note="Sin crédito vigente no hay saldo insoluto que mostrar." />
                      ),
              ),
        creditColumn(
            { key: 'status', label: 'Estado', width: COLUMN_WIDTH.status, sortKey: 'status' },
            (credit) => {
                const status = creditRowStatusOf(credit);
                return (
                    <span
                        title={rowStatusNote(status, credit.arrearsDays)}
                        className={cn(CHIP, 'cursor-help', CUSTOMER_STATUS_CHIP_CLASS[status])}
                    >
                        {customerStatusLabel(status, credit.arrearsDays)}
                    </span>
                );
            },
        ),
        creditColumn(
            {
                key: 'lastPayment',
                label: 'Últ. pago',
                width: COLUMN_WIDTH.lastPayment,
                sortKey: 'lastPayment',
                note: LAST_PAYMENT_COLUMN_NOTE,
            },
            (credit) => {
                const label = lastPaymentLabelOf(credit);
                return label === null ? (
                    <EmptyValue note={LAST_PAYMENT_EMPTY_NOTE} />
                ) : (
                    <CellText>{label}</CellText>
                );
            },
        ),
    ];
}

/** «Sin vigentes» dice menos en el listado que en la ficha: el BFF no trae las bajas contables. */
function rowStatusNote(status: CreditRowStatus, arrearsDays: number | null): string {
    if (status === 'CANCELLED') return CANCELLED_ROW_NOTE;
    return customerStatusNote(status, arrearsDays);
}

/**
 * Columna de crédito: pinta el dato si el BFF LEYÓ la cartera de esa fila y, si no, su marca de
 * estado. Nunca un cero, que se leería como un cliente que no debe nada.
 */
function creditColumn(
    column: Omit<
        DataTableColumn<CreditCustomerRow, SortKey>,
        'cell' | 'cellTitle' | 'cellClassName'
    >,
    render: (credit: CreditCustomerCredit) => ReactNode,
): DataTableColumn<CreditCustomerRow, SortKey> {
    return {
        ...column,
        cell: (row) => {
            const facts = creditRowFactsOf(row.credit);
            return facts.credit ? render(facts.credit) : <CreditGapMark gap={facts.gap} />;
        },
        cellTitle: (row) => {
            const facts = creditRowFactsOf(row.credit);
            return facts.credit ? undefined : CREDIT_ROW_GAP_NOTE[facts.gap];
        },
        cellClassName: (row) =>
            row.credit.state === 'READ' ? undefined : 'cursor-help text-muted-foreground/70',
    };
}

function BucketPill({
    label,
    title,
    value,
    valueClassName,
    on,
    onClick,
}: {
    label: string;
    title: string;
    value: string;
    valueClassName: string | undefined;
    on: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            aria-pressed={on}
            title={title}
            onClick={onClick}
            className={cn(
                'flex min-w-0 flex-1 items-baseline justify-center gap-[5px] whitespace-nowrap rounded-[5px] border px-2 py-1 transition-colors',
                on ? 'border-primary bg-accent' : 'border-border bg-card hover:bg-accent/60',
            )}
        >
            <span className="truncate text-[9.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                {label}
            </span>
            <span className={cn('truncate text-[12px] font-bold tabular-nums', valueClassName)}>
                {value}
            </span>
        </button>
    );
}

/** El motivo del hueco de Segmento, para el `title` de la celda. `undefined` cuando trae chips. */
function segmentGapNoteOf(row: CreditCustomerRow): string | undefined {
    if (!row.segmentsRead) return SEGMENTS_UNREAD_NOTE;
    if (row.segments.length === 0) return NO_SEGMENTS_NOTE;
    return undefined;
}

/**
 * La celda de Segmento. Con la lectura fallada lleva marca PROPIA: un guion ahí afirmaría que este
 * cliente no pertenece a ninguno, sin haber mirado.
 */
function SegmentCell({ row }: { row: CreditCustomerRow }) {
    if (!row.segmentsRead) {
        return (
            <CircleAlert
                role="img"
                aria-label={SEGMENTS_UNREAD_LABEL}
                className="inline-block size-3 align-[-2px] text-destructive"
            />
        );
    }
    if (row.segments.length === 0) return <EmptyValue note={NO_SEGMENTS_NOTE} />;
    return (
        <span className="flex flex-wrap gap-[3px]">
            {row.segments.map((segment) => (
                // El listado no publica el color del segmento: van todos con el slot de reserva.
                <SegmentPill
                    key={segment.segmentId}
                    name={segment.name}
                    colorSlot={FALLBACK_COLOR_SLOT}
                />
            ))}
        </span>
    );
}

/** Un dato LEÍDO que vino vacío: el guion tenue del mock, con su motivo en el `title`. */
/** La deuda total de sus vigentes y, debajo y en chico, el capital insoluto. */
function TotalDebtValue({ credit }: { credit: CreditCustomerCredit }) {
    if (!hasActiveCredit(credit)) {
        return <EmptyValue note="Sin crédito vigente no hay deuda que mostrar." />;
    }
    const debt = credit.totalDebtCents;
    const capital = credit.outstandingBalanceCents;
    return (
        <>
            <div>
                {typeof debt === 'number' ? (
                    formatMoneyShortCents(debt)
                ) : (
                    <EmptyValue note={TOTAL_DEBT_MISSING_NOTE} />
                )}
            </div>
            {capital === null ? null : (
                <div className="text-[9.5px] font-normal text-muted-foreground">
                    cap. {formatMoneyShortCents(capital)}
                </div>
            )}
        </>
    );
}

function EmptyValue({ note }: { note: string }) {
    return (
        <span title={note} className="cursor-help text-muted-foreground/70">
            {EMPTY_CELL}
        </span>
    );
}

/**
 * La marca de una celda de crédito SIN cifras. El BFF distingue «no tiene acreditado» de «no se
 * pudo leer», y las dos llevan icono propio: un guion mudo se leería como un cliente sin deuda.
 */
function CreditGapMark({ gap }: { gap: CreditRowGap }) {
    return (
        <CircleAlert
            role="img"
            aria-label={CREDIT_ROW_GAP_LABEL[gap]}
            className={cn(
                'inline-block size-3 align-[-2px]',
                gap === 'unavailable' ? 'text-destructive' : 'text-muted-foreground',
            )}
        />
    );
}
